import { InputRule, Mark, mergeAttributes, type JSONContent } from '@tiptap/core';

/**
 * Verknüpfter Text: ein Wort oder eine ganze Passage, die mit einem Eintrag verknüpft ist (Person, Ort, …). Im Editor blau,
 * gespeichert als `Wort[@schlüssel]` (ein Wort) bzw. `[ganze Passage](@schlüssel)`. Die Verknüpfung hängt am Text und verschwindet mit ihm.
 *
 * Entsteht, wenn man hinter ein Wort `@schlüssel` schreibt („Sarah @sarah_baier“ oder „Sarah@sarah_baier“ + Leerzeichen bzw. Satzzeichen), den Eintrag in
 * der @-Auswahl wählt oder Text markiert und „Verknüpfen“ wählt (Referenz-Browser).
 */

/** Wortzeichen (auch Umlaute, Ziffern, Bindestrich, Unterstrich) */
export const WORD = '[\\p{L}\\p{N}_-]';
const STORED = new RegExp(`^(${WORD}+)\\[@([A-Za-z][A-Za-z0-9_]{0,29})\\]`, 'u');
const STORED_START = new RegExp(`${WORD}+\\[@[A-Za-z]`, 'u');
/** Verknüpfte Passage `[Text](@schlüssel)` */
const PASSAGE = /^\[([^[\]\n]+)\]\(@([A-Za-z][A-Za-z0-9_]{0,29})\)/;
const PASSAGE_START = /\[[^[\]\n]+\]\(@[A-Za-z]/;
/** Ein einzelnes Wort (dann das kurze Format `Wort[@schlüssel]`) */
const SINGLE_WORD = new RegExp(`^${WORD}+$`, 'u');
/** „Wort @schlüssel“ bzw. „Wort@schlüssel“ und danach ein Leerzeichen bzw. Satzzeichen (am Cursor); Groß-/Kleinschreibung egal („@Elias“ = „@elias“) */
const TYPED = new RegExp(`(${WORD}+) ?@([A-Za-z][A-Za-z0-9_]{0,29})([\\s.,;:!?)"“”'])$`, 'u');

export interface RefLinkOptions {
  /** Gibt es diesen Schlüssel? (nur bekannte Schlüssel werden beim Tippen zur Verknüpfung) */
  isKnown: (key: string) => boolean;
  /** Schlüssel als Tooltip („@sarah“); in Dokumenten nicht – dort zeigt das Kontext-Panel das Element (RefGlance) */
  titles: boolean;
}

export const RefLink = Mark.create<RefLinkOptions>({
  name: 'refLink',
  inclusive: false, // weiterschreiben hinter dem Wort verlängert die Verknüpfung nicht
  // schließt nur andere Verknüpfungen aus: Formatierung (fett, kursiv) in einer verknüpften Passage bleibt
  addOptions() {
    return { isKnown: () => false, titles: true };
  },
  addAttributes() {
    const titles = this.options.titles;
    return {
      key: {
        default: null,
        parseHTML: (el) => el.getAttribute('data-ref'),
        renderHTML: (attrs) => ({ 'data-ref': attrs.key as string, ...(titles ? { title: `@${attrs.key as string}` } : {}) }),
      },
    };
  },
  parseHTML() {
    return [{ tag: 'span[data-ref]' }];
  },
  renderHTML({ HTMLAttributes }) {
    return ['span', mergeAttributes({ class: 'rt-ref-link' }, HTMLAttributes), 0];
  },

  // Markdown: „Wort[@schlüssel]“ bzw. „[ganze Passage](@schlüssel)“ (Formatierung in der Passage bleibt)
  markdownTokenizer: {
    name: 'refLink',
    level: 'inline',
    start: (src: string) => {
      const found = [STORED_START.exec(src)?.index, PASSAGE_START.exec(src)?.index].filter((i): i is number => i !== undefined);
      return found.length > 0 ? Math.min(...found) : -1;
    },
    tokenize: (src, _tokens, lexer) => {
      const p = PASSAGE.exec(src);
      if (p) return { type: 'refLink', raw: p[0], text: p[1]!, key: p[2]!.toLowerCase(), tokens: lexer.inlineTokens(p[1]!) };
      const m = STORED.exec(src);
      if (!m) return undefined;
      return { type: 'refLink', raw: m[0], text: m[1]!, key: m[2]!.toLowerCase() };
    },
  },
  parseMarkdown: (token, h) => {
    const key = (token as { key?: string }).key;
    return token.tokens && token.tokens.length > 0 ? h.applyMark('refLink', h.parseInline(token.tokens), { key }) : h.applyMark('refLink', [{ type: 'text', text: String(token.text ?? '') }], { key });
  },
  renderMarkdown: (node, h, ctx) => {
    const key = String((node.attrs as { key?: string } | undefined)?.key ?? '');
    const inner = h.renderChildren(node);
    // der ganze verknüpfte Text (beim Schreiben über mehrere Textstücke hinweg): ein Wort kurz, eine Passage in Klammern
    const text = (ctx as { meta?: { markText?: string } } | undefined)?.meta?.markText ?? inner;
    return SINGLE_WORD.test(text) ? `${inner}[@${key}]` : `[${inner}](@${key})`;
  },

  addInputRules() {
    return [
      new InputRule({
        find: TYPED,
        handler: ({ state, range, match }) => {
          const [, word, typed, trail] = match;
          const key = typed?.toLowerCase();
          if (!word || !key || !this.options.isKnown(key)) return null;
          const mark = state.schema.marks.refLink!.create({ key });
          state.tr.replaceWith(range.from, range.to, [state.schema.text(word, [mark]), state.schema.text(trail ?? ' ')]);
          return undefined;
        },
      }),
    ];
  },
});

// ---------------------------------------------------------------------------
// Einfache Textfelder (Kurzbeschreibung u. a.): reiner Text mit verknüpften Wörtern, Zeilenumbrüche bleiben
// ---------------------------------------------------------------------------

/** `Wort[@schlüssel]` bzw. eine Wendung `[der Magier](@schlüssel)` */
const STORED_ANY = new RegExp(`(${WORD}+)\\[@([A-Za-z][A-Za-z0-9_]{0,29})\\]|\\[([^[\\]\\n]+)\\]\\(@([A-Za-z][A-Za-z0-9_]{0,29})\\)`, 'gu');

/** Text eines Feldes → Editor-Inhalt (ein Absatz, Zeilenumbrüche als harte Umbrüche, `Wort[@schlüssel]` verknüpft; ältere `[Wendung](@schlüssel)` ebenso). */
export function fieldToDoc(text: string): JSONContent {
  const content: JSONContent[] = [];
  text.split('\n').forEach((line, i) => {
    if (i > 0) content.push({ type: 'hardBreak' });
    let last = 0;
    for (const m of line.matchAll(STORED_ANY)) {
      if (m.index! > last) content.push({ type: 'text', text: line.slice(last, m.index) });
      content.push({ type: 'text', text: m[1] ?? m[3]!, marks: [{ type: 'refLink', attrs: { key: (m[2] ?? m[4]!).toLowerCase() } }] });
      last = m.index! + m[0].length;
    }
    if (last < line.length) content.push({ type: 'text', text: line.slice(last) });
  });
  return { type: 'doc', content: [{ type: 'paragraph', content }] };
}

/**
 * Verknüpfter Text im Feld: Felder kennen keine Passagen (PLAN.md A105) – eine Wendung („der Magier“) steht als einzeln verknüpfte Wörter
 * (`der[@k] Magier[@k]`), die beim Sammeln als eine Wendung zählen. Nur wenn ein Teil kein reines Wort ist, bleibt die Passage.
 */
const linkedWords = (text: string, key: string): string => {
  const parts = text.split(/(\s+)/);
  return parts.every((p, i) => (i % 2 === 1 ? true : p === '' || SINGLE_WORD.test(p))) ? parts.map((p, i) => (i % 2 === 1 || p === '' ? p : `${p}[@${key}]`)).join('') : `[${text}](@${key})`;
};

/** Editor-Inhalt → Text eines Feldes (Umkehrung von `fieldToDoc`; Absätze werden Zeilen). */
export function docToField(doc: JSONContent): string {
  const lines: string[] = [];
  for (const para of doc.content ?? []) {
    let line = '';
    for (const n of para.content ?? []) {
      if (n.type === 'hardBreak') line += '\n';
      else if (n.type === 'text') {
        const link = n.marks?.find((m) => m.type === 'refLink');
        const t = n.text ?? '';
        const key = String(link?.attrs?.key ?? '');
        line += !link ? t : linkedWords(t, key);
      }
    }
    lines.push(line);
  }
  return lines.join('\n').replace(/\s+$/, '');
}
